import { useState } from 'react'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, Td, Th,
} from '@/components/erp/primitives'
import { CursorPager } from '@/shared/ui/CursorPager'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { useCursorPage } from '@/shared/hooks/useCursorPage'
import { fmtNumber, fmtPercent } from '@/shared/lib/format'
import { cn } from '@/lib/utils'
import type { OverlapResponse } from '@/shared/api/types'
import { analyticsMultiProgramme, analyticsOverlap } from '../api'
import { Truncation, Unmatchable } from '../parts'
import { BeneficiaryStatusBadge } from '../vocab'
import { AnalyticsScreen } from './shell'

/**
 * Who is in more than one programme.
 *
 * ## Jaccard, not a shared count
 *
 * Shading a matrix by `sharedCount` produces a picture of which two programmes
 * are biggest. The two largest programmes in any portfolio share the most
 * people because they contain the most people; that is arithmetic, not a
 * finding. `shared ÷ union` asks the question somebody actually has — *are
 * these two aimed at the same people* — and a small programme fully contained
 * in a large one lights up, which is the case worth seeing.
 *
 * ## The saving is not a saving
 *
 * `deduplicationSaving` is the difference between the sum of the programme
 * rows and the number of people. It is labelled "double-counted", not "saved":
 * nobody spent less money because two programmes reached the same household.
 * What it tells you is how far off a press release quoting the sum would be.
 */
export function ProgramOverlapScreen() {
  return (
    <AnalyticsScreen
      title="Overlap"
      description="People reached by more than one programme, matched on national ID."
      load={(f) => analyticsOverlap(f)}
      isEmpty={(d) => (d.programmes?.length ?? 0) === 0}
      exportKind="OVERLAP"
    >
      {(d) => <OverlapBody d={d} />}
    </AnalyticsScreen>
  )
}

function OverlapBody({ d }: { d: OverlapResponse }) {
  const [showGroups, setShowGroups] = useState(false)
  const programmes = d.programmes ?? []
  const cells = d.cells ?? []

  // The matrix is symmetric; the API sends one triangle. Index both ways so a
  // lookup does not have to know which of the two ids the server put first.
  const byPair = new Map<string, (typeof cells)[number]>()
  cells.forEach((c) => {
    byPair.set(`${c.programAId}|${c.programBId}`, c)
    byPair.set(`${c.programBId}|${c.programAId}`, c)
  })
  const peak = Math.max(...cells.map((c) => Number(c.jaccard) || 0), 0.0001)

  return (
    <div className="space-y-4">
      <KpiStrip>
        <MetricCard
          label="Distinct people"
          value={fmtNumber(d.distinctReach)}
          accent
          sub="Counted once, however many programmes they are in"
        />
        <MetricCard
          label="Sum of programme rows"
          value={fmtNumber(d.sumOfBeneficiaries)}
          sub="What adding the programmes up gives"
        />
        <MetricCard
          label="Double-counted"
          value={fmtNumber(d.deduplicationSaving)}
          sub={(
            <span title="Not money saved. This is how far a total built by adding programmes together would overstate the number of people.">
              the gap between the two figures
            </span>
          )}
        />
        <MetricCard
          label="Programme pairs"
          value={fmtNumber(cells.length)}
          sub={`of ${fmtNumber(d.totalAvailable)} computed`}
        />
      </KpiStrip>

      <Unmatchable count={d.unmatchableCount} />
      <Truncation truncatedTo={d.truncatedTo} totalAvailable={d.totalAvailable}
        orderedBy={d.orderedBy} noun="pairs" />

      <Note tone="info">
        The pair ranking is computed nightly across the whole portfolio. Filtering this
        screen selects rows out of that ranking — it does not re-rank what is left, so a
        filtered view can be missing a pair that would rank highly within the filter.
      </Note>

      <Panel
        title="Pairwise overlap"
        description="Shared ÷ union. A small programme sitting entirely inside a large one scores high; two big programmes sharing a tail do not."
        bodyClassName="p-0"
      >
        <ErpTable head={(
          <>
            <Th>Programme</Th>
            {programmes.map((p) => (
              <Th key={p.programId} className="text-right" title={p.name}>{p.code}</Th>
            ))}
          </>
        )}>
          {programmes.length === 0
            ? <EmptyRow colSpan={1} message="Fewer than two programmes are in scope, so there is nothing to compare." />
            : programmes.map((row) => (
              <tr key={row.programId}>
                <Td title={row.name}>
                  <span className="font-medium">{row.code}</span>
                  <span className="text-muted-foreground"> · {row.name}</span>
                </Td>
                {programmes.map((col) => {
                  if (col.programId === row.programId) {
                    return <Td key={col.programId} className="text-right text-muted-foreground/40">—</Td>
                  }
                  const c = byPair.get(`${row.programId}|${col.programId}`)
                  if (!c) {
                    return <Td key={col.programId} className="text-right text-muted-foreground/40">·</Td>
                  }
                  const j = Number(c.jaccard) || 0
                  return (
                    <Td key={col.programId} className="text-right">
                      <span
                        className={cn('inline-block rounded px-1.5 py-0.5 tabular-nums text-erp-xs')}
                        style={{
                          background: `color-mix(in oklab, var(--chart-1) ${Math.round((j / peak) * 70) + 6}%, transparent)`,
                        }}
                        title={`${fmtNumber(c.sharedCount)} shared · ${fmtNumber(c.aCount)} vs ${fmtNumber(c.bCount)}`}
                      >
                        {fmtPercent(j, 0)}
                      </span>
                    </Td>
                  )
                })}
              </tr>
            ))}
        </ErpTable>
        <p className="px-4 py-2 text-erp-xs text-muted-foreground border-t border-border">
          A dot means the pair was not in the nightly ranking, not that the two share nobody.
          Hover a cell for the shared count and the two programme sizes.
        </p>
      </Panel>

      <div>
        <button type="button" className="erp-btn" onClick={() => setShowGroups((v) => !v)}>
          {showGroups ? 'Hide' : 'Show'} people in several programmes
        </button>
      </div>
      {showGroups && <MultiProgrammePanel />}
    </div>
  )
}

/**
 * The people behind the matrix.
 *
 * `groupId` is synthetic and lives only for this response — it is never the
 * blind index, and it does not survive a reload. Nothing here shows a national
 * ID; the identity that produced the match is the thing being protected.
 */
function MultiProgrammePanel() {
  // `GET /overlap/multi-programme` takes a cursor and a limit and NOTHING else —
  // it is the one analytics endpoint that does not bind the filter bean. So this
  // list is portfolio-wide under the caller's own scope, and it says so rather
  // than appearing to respond to chips it never receives.
  const page = useCursorPage((cursor) =>
    analyticsMultiProgramme(cursor).then((p) => ({
      content: p.rows ?? [],
      nextCursor: p.nextCursor,
      hasMore: p.hasMore,
    })), [])

  return (
    <Panel title="People in more than one programme" bodyClassName="p-0"
      description="Portfolio-wide under your own scope. This list is the one analytics endpoint the filter bar does not reach.">
      <ErrorStrip message={page.error} className="m-4" />
      <ErpTable head={(
        <>
          <Th className="text-right">Programmes</Th>
          <Th>Enrolments</Th>
        </>
      )}>
        {page.rows.length === 0 && !page.loading
          ? <EmptyRow colSpan={2} message="Nobody in scope is enrolled in more than one programme." />
          : page.rows.map((r) => (
            <tr key={r.groupId}>
              <Td className="text-right tabular-nums font-medium">{r.programmeCount}</Td>
              <Td>
                <div className="flex flex-wrap gap-1.5">
                  {r.members.map((m) => (
                    <span key={m.beneficiaryId}
                      className="inline-flex items-center gap-1 rounded border border-border px-1.5 py-0.5 text-erp-xs">
                      {m.programName ?? m.programId}
                      <BeneficiaryStatusBadge code={m.statusCode} />
                    </span>
                  ))}
                </div>
              </Td>
            </tr>
          ))}
      </ErpTable>
      <CursorPager page={page.page} count={page.rows.length} hasMore={page.hasMore}
        canGoBack={page.canGoBack} onBack={page.back} onNext={page.next}
        loading={page.loading} unit="people" />
    </Panel>
  )
}
